import { useCallback, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { AdminDashboardRouteState } from "../admin-dashboard-route-state"
import type { AdminAreaEntry, AdminSearchEntry } from "../admin-search-registry"
import type { ResolvedManagementSectionEntry } from "../management-section-registry"

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { DialogDescription, DialogTitle } from "@/components/ui/dialog"

import { buildAdminSearchEntries, searchAdminEntries } from "../admin-search-registry"

type AdminGlobalSearchDialogProps = {
  open: boolean
  /** Capability-filtered sections — the same list the workspace renders from. */
  sections: ResolvedManagementSectionEntry[]
  /** Capability-filtered areas — the same list the navigation renders from. */
  areas?: AdminAreaEntry[]
  hasTenderRoute: boolean
  fairUseVisible?: boolean
  onOpenChange: (open: boolean) => void
  onNavigate: (target: AdminDashboardRouteState) => void
}

/**
 * Cross-area command palette for the admin dashboard: jumps to any area,
 * sub-view or management section.
 *
 * cmdk's own scorer is disabled (`shouldFilter={false}`) so filtering runs
 * through `searchAdminEntries`, which also matches the registries' English
 * search terms — the same deliberate choice the in-workspace setting picker
 * makes.
 */
export function AdminGlobalSearchDialog({
  open,
  sections,
  areas,
  hasTenderRoute,
  fairUseVisible = true,
  onOpenChange,
  onNavigate,
}: AdminGlobalSearchDialogProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState("")

  const entries = useMemo(
    () =>
      buildAdminSearchEntries(sections, hasTenderRoute, { visibleAreas: areas, fairUseVisible }),
    [sections, hasTenderRoute, areas, fairUseVisible]
  )

  const getLabel = useCallback(
    (entry: AdminSearchEntry) => t(entry.labelKey, entry.labelFallback),
    [t]
  )
  const matches = useMemo(
    () => searchAdminEntries(entries, query, getLabel),
    [entries, query, getLabel]
  )

  // Preserve registry order within each group, and group order by first
  // appearance, so the palette reads in the same order as the tab strip.
  const groups = useMemo(() => {
    const byGroup = new Map<string, { heading: string; items: AdminSearchEntry[] }>()
    for (const entry of matches) {
      const existing = byGroup.get(entry.groupKey)
      if (existing) existing.items.push(entry)
      else
        byGroup.set(entry.groupKey, {
          heading: t(entry.groupKey, entry.groupFallback),
          items: [entry],
        })
    }
    return [...byGroup.values()]
  }, [matches, t])

  const handleOpenChange = (next: boolean) => {
    if (!next) setQuery("")
    onOpenChange(next)
  }

  const handleSelect = (entry: AdminSearchEntry) => {
    // Close before navigating: a jump made while a section draft is dirty
    // trips the router blocker, and its dialog must not open behind this one.
    handleOpenChange(false)
    onNavigate(entry.target)
  }

  return (
    <CommandDialog
      /*
       * `label` names the combobox: cmdk renders a visually-hidden <label> on
       * the Command root and points the input's `aria-labelledby` at it, and
       * that reference beats `aria-label` in the accessible-name calculation —
       * so an `aria-label` on CommandInput would leave the input unnamed.
       */
      open={open}
      commandProps={{
        label: t("adminDashboard.globalSearch.title", "Search the admin dashboard"),
        shouldFilter: false,
      }}
      onOpenChange={handleOpenChange}
    >
      <DialogTitle className="sr-only">
        {t("adminDashboard.globalSearch.title", "Search the admin dashboard")}
      </DialogTitle>
      <DialogDescription className="sr-only">
        {t("adminDashboard.globalSearch.description", "Jump to any admin area, view or setting.")}
      </DialogDescription>
      <CommandInput
        value={query}
        placeholder={t(
          "adminDashboard.globalSearch.placeholder",
          "Search areas, views and settings…"
        )}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {t("adminDashboard.globalSearch.noResults", "Nothing matches that search.")}
        </CommandEmpty>
        {groups.map((group) => (
          <CommandGroup key={group.heading} heading={group.heading}>
            {group.items.map((entry) => {
              const Icon = entry.icon
              return (
                <CommandItem
                  key={entry.key}
                  className="min-h-11 gap-3"
                  value={entry.key}
                  onSelect={() => handleSelect(entry)}
                >
                  <Icon aria-hidden="true" className="text-muted-foreground size-4" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{getLabel(entry)}</span>
                    <span className="text-muted-foreground block truncate text-xs">
                      {t(entry.subtitleKey, entry.subtitleFallback)}
                    </span>
                  </span>
                </CommandItem>
              )
            })}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  )
}
